import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ENDPOINTS, apiCall } from '../utils/api.js';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Avatar,
  List,
  Link,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Navbar from '../components/Navbar.js';
import Footer from '../components/Footer.js';




const PLATFORM_FEATURES = [
  { icon: <PeopleOutlineIcon />, text: 'Customers join the queue from their phone — no app needed' },
  { icon: <ScheduleOutlinedIcon />, text: 'Live position and estimated wait visible at all times' },
  { icon: <NotificationsNoneOutlinedIcon />, text: 'Automatic notifications when their turn is near' },
  { icon: <DashboardOutlinedIcon />, text: 'Staff manage walk-ins and scheduled bookings from one dashboard' },
  { icon: <AdminPanelSettingsOutlinedIcon />, text: 'Role-based admin with configurable operating hours' },
  { icon: <PublicOutlinedIcon />, text: 'Multi-timezone support built in from day one' },
];

const isCheckout = process.env.REACT_APP_IS_CHECKOUT  === 'true';

// Icon key (Package.icon) -> pricing-card icon.
const PACKAGE_ICONS = {
  rocket: RocketLaunchOutlinedIcon,
  store: StorefrontOutlinedIcon,
  building: BusinessOutlinedIcon,
};

// "N locations" from the structured max_locations field (null = unlimited).
const locationText = (n) => {
  if (n == null) return 'Unlimited locations';
  return `${n} location${Number(n) === 1 ? '' : 's'}`;
};

// A notification-channel bullet derived from the structured allowed/limit fields
// (0/null limit = unlimited). Not allowed => a red ✗ "No X notifications".
const notifBullet = (allowed, limit, label) => {
  if (!allowed) return { text: `No ${label} notifications`, included: false };
  const text = limit
    ? `Up to ${Number(limit).toLocaleString()} ${label} notifications / month`
    : `Unlimited ${label} notifications`;
  return { text, included: true };
};

// Fallback tiers if /api/packages/ can't be reached (keeps the page presentable).
const FALLBACK_PACKAGES = [
  { slug: 'starter', name: 'Starter', tagline: 'Perfect for small clinics & solo operators', monthly_price: 20, icon: 'rocket', is_featured: false, max_locations: 1, trial_days: 15, sms_allowed: false, sms_limit: 0, email_allowed: true, email_limit: 1000, feature_bullets: [ { text: 'Analytics dashboard', included: true } ] },
  { slug: 'growth', name: 'Growth', tagline: 'For salons, restaurants & growing teams', monthly_price: 50, icon: 'store', is_featured: true, max_locations: 1, trial_days: 15, sms_allowed: true, sms_limit: 1000, email_allowed: true, email_limit: 5000, feature_bullets: [{ text: 'SMS & Email notifications', included: true },  { text: 'Analytics dashboard', included: true }] },
  { slug: 'enterprise', name: 'Enterprise', tagline: 'For hospital networks & multi-branch ops', monthly_price: 89, icon: 'building', is_featured: false, max_locations: null, trial_days: 15, sms_allowed: true, sms_limit: 5000, email_allowed: true, email_limit: 20000, feature_bullets: [{ text: 'SMS & Email notifications', included: true },  { text: 'Analytics dashboard', included: true }] },
];

// One pricing card, rendered entirely from a backend Package (admin-editable).
function PricingCard({ pkg, onStart }) {
  const Icon = PACKAGE_ICONS[pkg.icon] || RocketLaunchOutlinedIcon;
  const featured = !!pkg.is_featured;
  // Bullets are all structured: locations + SMS/email (from allowed/limit) + the
  // remaining free-text feature bullets (walk-in, live queue, analytics, …).
  const bullets = [
    { text: locationText(pkg.max_locations), included: true },
    notifBullet(pkg.email_allowed, pkg.email_limit, 'email'),
    notifBullet(pkg.sms_allowed, pkg.sms_limit, 'SMS'),
    ...(Array.isArray(pkg.feature_bullets) ? pkg.feature_bullets : []),
  ];
  const trialDays = pkg.trial_days || 15;
  return (
    <Card
      sx={{
        margin: 'auto', minWidth: 260, maxWidth: 360, display: 'flex', flexDirection: 'column',
        border: featured ? '2px solid' : '1px solid',
        borderColor: featured ? 'primary.main' : 'divider',
        transition: 'transform 0.18s, box-shadow 0.18s', position: 'relative', overflow: 'visible',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' },
      }}
    >
      {featured && (
        <Chip
          label="Most popular" color="primary" size="small"
          sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', fontWeight: 700, color: '#fff' }}
        />
      )}
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', p: 3, '&:last-child': { pb: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 44, height: 44, bgcolor: featured ? 'primary.main' : 'action.hover', color: featured ? '#fff' : 'text.secondary' }}>
            <Icon sx={{ fontSize: 26 }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={800}>{pkg.name}</Typography>
            <Typography variant="caption" color="text.secondary">{pkg.tagline}</Typography>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
          <Typography variant="h4" fontWeight={900} color={featured ? 'primary.main' : 'text.primary'}>
            ${Number(pkg.monthly_price)}
          </Typography>
          <Typography variant="body2" color="text.secondary">per month</Typography>
        </Box>

        <List dense disablePadding sx={{ flex: 1 }}>
          {bullets.map((b, i) => (
            <ListItem key={i} disableGutters disablePadding sx={{ py: 0.4 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {b.included
                  ? <CheckCircleOutlineIcon sx={{ fontSize: 17, color: 'success.main' }} />
                  : <HighlightOffIcon sx={{ fontSize: 17, color: 'error.main' }} />}
              </ListItemIcon>
              <ListItemText primary={<Typography component="span" variant="body2" color="text.secondary">{b.text}</Typography>} />
            </ListItem>
          ))}
        </List>

        <Button
          variant={featured ? 'contained' : 'outlined'} color="primary" fullWidth endIcon={<ArrowForwardIcon />}
          onClick={(e) => { e.preventDefault(); onStart(pkg.slug); }}
          sx={{ mt: 1, borderRadius: 2, fontWeight: 700, ...(featured ? { color: '#fff' } : {}) }}
        >
          Start {trialDays}-day trial
        </Button>
      </CardContent>
    </Card>
  );
}

export default function BusinessHomePage() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  // Pricing tiers come from the backend so admin edits / new packages show up here.
  const [packages, setPackages] = useState(FALLBACK_PACKAGES);
  useEffect(() => {
    let alive = true;
    apiCall(ENDPOINTS.PACKAGES).then((d) => {
      if (!alive) return;
      const list = (d && d.packages) || [];
      if (list.length) setTimeout(() => setPackages(list), 0);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Remember the tier a visitor picked here so the onboarding form can default to
  // it (it survives the enquiry -> email -> checkout round-trip within this browser).
  const startSignup = (slug) => {
    try { if (slug) localStorage.setItem('sqip:selectedPackage', slug); } catch (e) { /* ignore */ }
    navigate(isCheckout ? '/checkout' : '/business/form');
  };

  const handleVideoToggle = () => {
    if (!videoRef.current) return;
    if (videoPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setVideoPlaying((v) => !v);
  };


  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Navbar />

      <Box
        sx={{
          maxWidth: 900,
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          py: { xs: 5, sm: 8 },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 6, sm: 8 },
        }}
      >

        {/* ── HERO ──────────────────────────────────────────────────── */}
        <Fade in timeout={400}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              fontWeight={900}
              sx={{
                lineHeight: 1.15,
                mb: 2,
                letterSpacing: '-0.02em',
                color: 'text.primary',
                fontSize: { xs: '2rem', sm: '2.75rem' },
              }}
            >
              We organise,{' '}
              <Box
                component="span"
                sx={{
                  color: 'primary.main',
                  background: (theme) =>
                    theme.palette.custom?.gradientPrimary || theme.palette.primary.main,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
      you thrive
              </Box>
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              fontWeight={400}
              sx={{ maxWidth: 600, mx: 'auto', lineHeight: 1.6, mb: 3.5 }}
            >
Every client who walks out of your waiting room is a customer lost.  Lets clients join on your businesses page. Your front desk’s burden gets simplified saving you time and money. Book for a free demo.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={(e) => {
                  e.preventDefault();
                  startSignup('growth');
                }}
                sx={{ borderRadius: 3, px: 4, fontWeight: 700, color: '#fff' }}
              >
                Book today
              </Button>
            </Box>
          </Box>
        </Fade>

        {/* ── DEMO VIDEO ────────────────────────────────────────────── */}
        <Fade in timeout={500}>
          <Box>
            <Box
              sx={{
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
                aspectRatio: '9 / 16',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                margin: 'auto',
                maxWidth: 500,
                width: { xs: 200, sm: 300 }, 
              }}
              onClick={handleVideoToggle}
            >
              {/* Replace `src` with your actual video file or embed URL */}
              <video
                ref={videoRef}
                src="/demo.mp4"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                playsInline
                onEnded={() => setVideoPlaying(false)}
              />

              {/* Overlay play button when paused */}
              {!videoPlaying && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0,0,0,0.38)',
                    gap: 1.5,
                  }}
                >
                  <PlayCircleOutlineIcon sx={{ fontSize: 72, color: '#fff', opacity: 0.9 }} />
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                    Watch the demo
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', textAlign: 'center', mt: 1 }}
            >
              3-minute product walkthrough
            </Typography>
          </Box>
        </Fade>

        {/* ── WHAT IS sqip ──────────────────────────────────────────── */}
        <Fade in timeout={500}>
          <Box>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{ mb: 1.5, letterSpacing: '-0.01em' }}
            >
              What is sqip?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3.5, lineHeight: 1.75 }}>
              sqip is a virtual queue platform built for clinics, salons, restaurants, and any
              service business where people wait. Customers join from their phone and see their live
              position and estimated wait — no app install required. When their turn is close, they
              get notified automatically. Meanwhile, your staff runs everything from a single
              live dashboard: walk-ins, scheduled appointments, role assignments, and configurable
              hours. It was built end-to-end by an engineering team that was tired of
              watching people stare at waiting-room walls.
            </Typography>

            {/* Feature list */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.5,
              }}
            >
              {PLATFORM_FEATURES.map((f, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    p: 1.75,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box
                    sx={{
                      mt: '2px',
                      color: 'primary.main',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {f.icon}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                    {f.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Fade>

        {/* ── PLANS ─────────────────────────────────────────────────── */}
        <Fade in timeout={600}>
          <Box>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{ mb: 1, letterSpacing: '-0.01em' }}
              >
                  Packages
              </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', textAlign: 'center', mt: 1 }}
            >
              All plans priced in CAD.
            </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                alignItems: { xs: 'stretch', md: 'flex-start' },
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              {packages.map((p) => (
                <PricingCard key={p.slug} pkg={p} onStart={startSignup} />
              ))}
            </Box>

            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', textAlign: 'center', mt: 2.5 }}
            >
              All plans include SSL, 99.9% uptime.
            </Typography>
            <Link href="/legal/business-terms" sx={{
            fontSize: "13px",
            display: "block",
            color: 'primary.main',
            textDecoration: 'none',
            textAlign: "center",
            paddingTop: "5px",
            }}>
             Terms and Conditions Apply
            </Link>

          </Box>
        </Fade>

        {/* ── FOOTER CTA ────────────────────────────────────────────── */}
        <Fade in timeout={600}>
          <Box
            sx={{
              background: (theme) =>
                theme.palette.custom?.gradientPrimary || theme.palette.primary.main,
              borderRadius: 4,
              px: { xs: 3, sm: 5 },
              py: { xs: 4, sm: 5 },
              textAlign: 'center',
              color: '#fff',
            }}
          >
            <Typography variant="h5" fontWeight={900} sx={{ mb: 1.5 }}>
              Ready to end the paper-list era?
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.85, mb: 3 }}>
              Set up takes under 15 minutes.
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
               onClick={(e) => {
                  e.preventDefault();
                  navigate(isCheckout ? "/checkout" : "/business/form");
                }}
              sx={{
                bgcolor: '#fff',
                color: 'primary.main',
                fontWeight: 700,
                borderRadius: 3,
                px: 4,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              }}
            >
              Create your account today
            </Button>
          </Box>
        </Fade>

      </Box>
    <Footer />
    </Box>
  );
}
